// @flow

// These helpers are meant to be used by reducers that handle processing of standard QCS "sync"
// resources.
//
// Tried to define proper flow types, but had trouble with the proper typing of records since
// the actual resource record is of different types. So, using any type to bypass type checking.
// This means we will not catch errors if expected properties are not found in the store or

import { Map as ImmutableMap, Record, isKeyed, isIndexed, Set as SetImmutable, OrderedSet, getIn, set } from 'immutable';
import * as undoActions from 'data/undo/undoActions';
import { mkUndo } from 'data/undo/undoTypes';
import { v4 as uuidv4 } from 'uuid';

import type { QcsSyncResourceStore, QcsSyncResourcesPayload } from './qcsSyncResourceTypes';
import { mkQcsSyncResourcesPayload } from './qcsSyncResourceTypes';

// mark resource as invalid and save the error
export function completeWithError(state: any, error: any) {
  return state.merge({
    error,
    isLoading: false,
  });
}

// mark resources as invalid/outdated
export function resetResources(state, error) {
  return state.merge({
    lastSyncDate: null,
    loadPending: true,
    error,
  });
}

// doUpsertResource
//
// Upsert a resource into the resourcesById map.
//
// Returns the updated resourcesById map.
//
const doUpsertResource = (resourcesById: ImmutableMap<string, any>, resourceToUpsert: any, doMerge) => {
  let updatedResourcesById = resourcesById;
  const id = resourceToUpsert.id || resourceToUpsert.clientId;
  if (!resourceToUpsert.isDeleted && !resourceToUpsert.isNotSynced) {
    const existingResource = resourcesById.get(id);
    if (!doMerge || !existingResource) {
      updatedResourcesById = resourcesById.set(id, resourceToUpsert);
    } else {
      updatedResourcesById = resourcesById.set(id, mergeDeepEx(existingResource, resourceToUpsert));
    }
  } else {
    updatedResourcesById = resourcesById.delete(id);
  }
  if (resourceToUpsert.id && resourceToUpsert.clientId) {
    // try and delete any resource by clientId once we update, safe to delete even if it doesn't exist
    updatedResourcesById = updatedResourcesById.delete(resourceToUpsert.clientId);
  }
  return updatedResourcesById;
};

// ================================================================================================
// upsertResource
//
// Optimized to upsert a single resource into the resources store. This method is generally called
// by the reducer after creating/updating/deleting a single resource. In this case, the default
// doMerge setting will doMerge in any changes from the payload sent to QCS. If this method is called
// for replacing a resource (after receiving from QCS), then doMerge should be set to false.
//
// Returns the updated state.
// ================================================================================================
export function upsertResource(
  state: any,
  resourceToUpsert: any,
  doMerge: boolean = true,
  isLoading: boolean = false,
) {
  return state.merge({
    resourcesById: doUpsertResource(state.resourcesById, resourceToUpsert, doMerge),
    isLoading,
  });
}

// upsertResources
//
// Optimized to upsert multiple sync resources into a sync resource store.
//
export function upsertResources(state: QcsSyncResourceStore, data: QcsSyncResourcesPayload): QcsSyncResourceStore {
  const {
    resources,
    asyncDispatch = undefined,        // SYNC_SIDE_EFFECTS
    syncActions,          // SYNC_SIDE_EFFECTS
    doMerge = false,
    lastSyncDate,
    error = undefined,
  } = data;

  // optimize when upserting 0 or 1 accounts
  //
  if (!resources || resources.length === 0) {
    return state.merge({
      loadPending: false,
      isLoading: false,
      error,
    });
  }
  // check all resources for sync notifications required
  // this only occurs if the reducer calling upsert includes these actions in the payload
  // SYNC_SIDE_EFFECTS
  if (state.resourceName && asyncDispatch && syncActions) {
    resources.forEach((res) => {
      if (res.isDeleted || res.isNotSynced && syncActions.syncDeleteAction) {
        asyncDispatch(syncActions.syncDeleteAction(res))
      }
    })
  }
  if (resources.length === 1) {
    return state.merge({
      resourcesById: doUpsertResource(state.loadPending ? ImmutableMap() : state.resourcesById, resources[0], doMerge),
      loadPending: false,
      isLoading: false,
      error,
      lastSyncDate: lastSyncDate || state.lastSyncDate,
    });
  }

  return state.merge({
    resourcesById: (state.loadPending ? ImmutableMap() : state.resourcesById).withMutations((resourcesById) => {
      resources.forEach((resource) => {
        doUpsertResource(resourcesById, resource, doMerge);
      });
    }),
    loadPending: false,
    isLoading: false,
    error,
    lastSyncDate: lastSyncDate || state.lastSyncDate,
  });
}

export function makeUndoResource(existingResource: any, newResource: any, afterUpdate: ?boolean) {
  let undoResource;

  if (newResource.isDeleted && existingResource && !afterUpdate) { // action=delete => undoAction=create
    undoResource = existingResource.delete('id');
    undoResource = undoResource.set('clientId', uuidv4().toUpperCase());
  } else if (!existingResource && !newResource.isDeleted && (afterUpdate === undefined || afterUpdate)) { // action=create => undoAction=delete
    undoResource = newResource && newResource.set('isDeleted', true);
  } else if (!afterUpdate) { // action=update => undoAction=update
    undoResource = existingResource;
  }

  return undoResource;
}

export function setupUndo(asyncDispatch: any, userMessage: string, executeActions: ?Array, ) {
  if (asyncDispatch && userMessage) {
    const undo = mkUndo({
      actions: executeActions,
      userMessage,
      key: uuidv4(),
    });
    asyncDispatch(undoActions.addUndo(undo));
  }
}

export function setupUndoForAction(actionToUndo: any, executeActions: ?Array, ) {
  if (actionToUndo && actionToUndo.asyncDispatch && actionToUndo.meta && actionToUndo.meta.undo && actionToUndo.meta.undo.userMessage) {
    setupUndo(actionToUndo.asyncDispatch, actionToUndo.meta.undo.userMessage, executeActions);
  }
}

export function upsertResourceSetupUndo(state: QcsSyncResourceStore, action: any, undoActionCreator: any) {
  if (action && action.meta && action.meta.undo && action.meta.undo.userMessage) {
    const existingResource = state.resourcesById.get(action.payload.id);
    const undoResource = action.payload && makeUndoResource(existingResource, action.payload, undefined);
    if (undoResource) {
      const { undo, ...meta } = action.meta || {};
      setupUndoForAction(action, undoActionCreator && undoResource ? [undoActionCreator(undoResource, meta)] : []);
    }
  }

  return upsertResource(state, action.payload);
}

export function upsertResourcesSetupUndo(state: QcsSyncResourceStore, action: any, undoActionCreator: any, afterUpdate: boolean = false) {
  const { payload: resources } = action;
  if (action && action.asyncDispatch && action.meta && action.meta.undo && action.meta.undo.userMessage) {
    const undoResources = [];
    if (undoActionCreator && state.resourcesById) {
      resources.forEach((resource) => {
        const existingResource = state.resourcesById.get(resource.id);
        const undoResource = makeUndoResource(existingResource, resource, afterUpdate);
        if (undoResource) {
          undoResources.push(undoResource);
        }
      });
    }
    const { undo, ...meta } = action.meta || {};
    setupUndoForAction(action, undoActionCreator && undoResources && undoResources.length ? [undoActionCreator(undoResources, meta)] : []);
  }

  const data = mkQcsSyncResourcesPayload({
    resources,
    doMerge: true,
  });
  return upsertResources(state, data);
}

function resolveConflictAndContinue(curObj, key, value, newObj, basePath) {
  let retObj = curObj;

  const path = [...basePath, key];
  if (Array.isArray(value)
    || isIndexed(value)
    || SetImmutable.isSet(value)
    || OrderedSet.isOrderedSet(value)) { // erase arrays or simple collections
    const newValue = getIn(newObj, path);
    if (newValue) {
      retObj = set(retObj, key, null);
    }
  } else if (typeof value === 'object' && value !== null && key) {
    const retValue = resolveConflicts(value, newObj, path);
    retObj = set(retObj, key, retValue);
  }

  return retObj;
}

function resolveConflicts(curObj, newObj, basePath = []) {
  let retObj = curObj;

  if ((Record.isRecord(curObj) || isKeyed(curObj)) && curObj[Symbol.iterator]) { // iterate over keyed immutables
    // eslint-disable-next-line no-restricted-syntax
    for (const prop of curObj) {
      if (Array.isArray(prop) && prop.length === 2) {
        const [key, value] = prop;
        retObj = resolveConflictAndContinue(retObj, key, value, newObj, basePath);
      }
    }
  } else if (typeof curObj === 'object' && curObj !== null) { // iterate over js objects
    // eslint-disable-next-line guard-for-in,no-restricted-syntax
    for (const key in curObj) {
      const value = curObj[key];
      retObj = resolveConflictAndContinue(retObj, key, value, newObj, basePath);
    }
  }

  return retObj;
}

export function mergeDeepEx(oldObj: Object, newObj: Object) {
  const oldObjResolved = resolveConflicts(oldObj, newObj);
  return oldObjResolved.mergeDeepWith(
    (oldValue, newValue) => newValue === undefined ? oldValue : newValue,
    newObj,
  );
}
